/**
 * Vacation Mode dates.
 *
 * The return date is a calendar date the user picks. The server stores it as
 * 00:00Z of that date (`vacationUntil`) and vacation ends at that instant.
 *
 * Two consequences this file exists to handle:
 *  - DISPLAY: 00:00Z is the evening before in the Americas, so formatting
 *    `vacationUntil` in the viewer's timezone would show the day BEFORE the
 *    one picked. Every date shown goes through formatVacationDate, which
 *    formats in UTC and therefore shows the date that was picked.
 *  - PICKING: a date is only valid while its 00:00Z is still in the future.
 *    In a US evening, local tomorrow has already started in UTC, so the
 *    earliest pickable date is the later of local tomorrow and UTC tomorrow.
 */

export const VACATION_MAX_DAYS = 90;

export type VacationQuickPick = '1w' | '2w' | '1m';

export const VACATION_DEFAULT_PICK: VacationQuickPick = '2w';

const pad = (n: number) => String(n).padStart(2, '0');

/** A local calendar date as YYYY-MM-DD (what PUT /settings/vacation takes). */
export const toDateString = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const utcDateString = (date: Date): string =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

const localDatePlusDays = (now: Date, days: number): Date =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);

const utcDatePlusDays = (now: Date, days: number): string =>
  utcDateString(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days)));

/** YYYY-MM-DD strings compare correctly as text. */
const later = (a: string, b: string) => (a > b ? a : b);
const earlier = (a: string, b: string) => (a < b ? a : b);

/** Earliest return date: the first date, not before local tomorrow, whose 00:00Z is still ahead. */
export const minReturnDate = (now: Date = new Date()): string =>
  later(toDateString(localDatePlusDays(now, 1)), utcDatePlusDays(now, 1));

/** Latest return date: local today + 90, never past what the server accepts (UTC today + 91). */
export const maxReturnDate = (now: Date = new Date()): string =>
  earlier(toDateString(localDatePlusDays(now, VACATION_MAX_DAYS)), utcDatePlusDays(now, VACATION_MAX_DAYS + 1));

const clampReturnDate = (date: string, now: Date): string =>
  earlier(later(date, minReturnDate(now)), maxReturnDate(now));

/** The return date for a quick pick, counted on the user's local calendar. */
export const quickPickDate = (pick: VacationQuickPick, now: Date = new Date()): string => {
  let target: Date;
  if (pick === '1w') target = localDatePlusDays(now, 7);
  else if (pick === '2w') target = localDatePlusDays(now, 14);
  else {
    // One calendar month; Jan 31 lands on the last day of February, not in March.
    const day = now.getDate();
    const lastOfNext = new Date(now.getFullYear(), now.getMonth() + 2, 0).getDate();
    target = new Date(now.getFullYear(), now.getMonth() + 1, Math.min(day, lastOfNext));
  }
  return clampReturnDate(toDateString(target), now);
};

/** The date part of a stored `vacationUntil` ISO string (its UTC calendar date). */
export const vacationUntilToDateString = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : utcDateString(date);
};

/**
 * The picked date, for display: "Tue, Oct 20" (or "Oct 20" with weekday off).
 * Formatted in UTC on purpose; see the note at the top of this file.
 */
export const formatVacationDate = (
  iso: string | null | undefined,
  { weekday = true }: { weekday?: boolean } = {},
): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    ...(weekday ? { weekday: 'short' } : {}),
    month: 'short',
    day: 'numeric',
  }).format(date);
};

/** "until Tue, Oct 20" or "until you turn it off". */
export const vacationUntilPhrase = (iso: string | null | undefined): string =>
  iso ? `until ${formatVacationDate(iso)}` : 'until you turn it off';
